import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute, Link } from "wouter";
import { apiRequest, queryClient } from "../lib/api";
import { formatDateTime } from "../lib/utils";
import { ArrowLeft, Send } from "lucide-react";

interface TicketDetail {
  ticket: {
    id: string;
    subject: string;
    category: string;
    priority: string;
    status: string;
    assigned_to: string | null;
    last_reply_at: string | null;
    resolved_at: string | null;
    created_at: string;
    updated_at: string;
    user_id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    telegram_username: string | null;
    telegram_chat_id: string | null;
    phone: string | null;
    source: string | null;
    client_id: string | null;
    client_name: string | null;
    client_slug: string | null;
  };
  messages: Array<{
    id: string;
    ticket_id: string;
    user_id: string | null;
    is_support: boolean;
    content: string;
    created_at: string;
    user_email: string | null;
    user_first_name: string | null;
    attachments?: { telegram?: { chatId: string; messageId: string } } | null;
  }>;
}

interface AdminListItem {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
}

function getStatusBadge(status: string) {
  const map: Record<string, { label: string; className: string }> = {
    open: { label: "Открыт", className: "bg-blue-100 text-blue-700" },
    in_progress: { label: "В работе", className: "bg-yellow-100 text-yellow-700" },
    waiting_user: { label: "Ожидает ответа", className: "bg-orange-100 text-orange-700" },
    resolved: { label: "Решён", className: "bg-green-100 text-green-700" },
    closed: { label: "Закрыт", className: "bg-gray-100 text-gray-600" },
  };
  const item = map[status] || { label: status, className: "bg-gray-100 text-gray-600" };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${item.className}`}>
      {item.label}
    </span>
  );
}

function getPriorityBadge(priority: string) {
  const map: Record<string, { label: string; className: string }> = {
    low: { label: "Низкий", className: "bg-slate-100 text-slate-600" },
    normal: { label: "Обычный", className: "bg-blue-50 text-blue-600" },
    high: { label: "Высокий", className: "bg-orange-100 text-orange-700" },
    urgent: { label: "Срочный", className: "bg-red-100 text-red-700" },
  };
  const item = map[priority] || { label: priority, className: "bg-gray-100 text-gray-600" };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${item.className}`}>
      {item.label}
    </span>
  );
}

function getCategoryLabel(category: string) {
  const map: Record<string, string> = {
    general: "Общий",
    billing: "Биллинг",
    technical: "Техническая",
    feature_request: "Предложение",
  };
  return map[category] || category;
}

function getSourceLabel(source: string | null | undefined) {
  const map: Record<string, string> = {
    lk: "Личный кабинет",
    telegram: "Telegram",
    max: "Max",
    manual: "Вручную (админка)",
    escalation: "Эскалация",
  };
  return map[source || ""] || source || "—";
}

// Рендерит вложение клиента (скрин/документ) из ассистент-чата Telegram.
// <img src> не умеет слать Authorization → грузим blob с admin-токеном и делаем object URL.
function MediaAttachment({ chatId, messageId }: { chatId: string; messageId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [isImage, setIsImage] = useState(true);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("admin_token");
        const res = await fetch(
          `/api/admin/assistant/media?chatId=${encodeURIComponent(chatId)}&messageId=${encodeURIComponent(messageId)}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const ct = res.headers.get("content-type") || "";
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        setIsImage(ct.startsWith("image/"));
        setStatus("ready");
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [chatId, messageId]);

  if (status === "loading") {
    return <div className="text-xs text-muted-foreground mt-2">Загрузка вложения…</div>;
  }
  if (status === "error" || !url) {
    return <div className="text-xs text-muted-foreground mt-2">📎 Вложение недоступно</div>;
  }
  if (isImage) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="inline-block mt-2" title="Открыть в полном размере">
        <img
          src={url}
          alt="Вложение клиента"
          className="max-h-72 rounded border border-border object-contain hover:opacity-90 transition-opacity"
        />
      </a>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 mt-2 text-sm text-primary hover:underline"
    >
      📎 Скачать вложение
    </a>
  );
}

export default function TicketDetailPage() {
  const [, params] = useRoute("/tickets/:id");
  const ticketId = params?.id;

  const [replyText, setReplyText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedAdmin, setSelectedAdmin] = useState("");

  const { data, isLoading } = useQuery<TicketDetail>({
    queryKey: [`/api/admin/tickets/${ticketId}`],
    enabled: !!ticketId,
  });

  const { data: adminsList } = useQuery<AdminListItem[]>({
    queryKey: ["/api/admin/admins-list"],
  });

  const ticket = data?.ticket;
  const messages = data?.messages || [];

  useEffect(() => {
    if (ticket) {
      setSelectedStatus(ticket.status);
      setSelectedAdmin(ticket.assigned_to || "");
    }
  }, [ticket]);

  const replyMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/admin/tickets/${ticketId}/reply`, {
        message: replyText,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/admin/tickets/${ticketId}`] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/tickets/stats"] });
      setReplyText("");
    },
  });

  const statusMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("PATCH", `/api/admin/tickets/${ticketId}/status`, {
        status: selectedStatus,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/admin/tickets/${ticketId}`] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/tickets/stats"] });
    },
  });

  const assignMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("PATCH", `/api/admin/tickets/${ticketId}/assign`, {
        adminId: selectedAdmin || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/admin/tickets/${ticketId}`] });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-muted rounded w-64 animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-lg border border-border bg-card p-4 animate-pulse">
              <div className="h-4 bg-muted rounded w-24 mb-2" />
              <div className="h-6 bg-muted rounded w-32" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-20 text-muted-foreground">
        Тикет не найден
      </div>
    );
  }

  const isClosed = ticket.status === "closed";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Link href="/tickets">
          <button className="mt-1 p-1.5 rounded hover:bg-muted transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold">{ticket.subject}</h1>
          <div className="flex items-center gap-2 mt-1">
            {getStatusBadge(ticket.status)}
            {getPriorityBadge(ticket.priority)}
          </div>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-sm text-muted-foreground">Пользователь</div>
          <div className="text-sm font-medium mt-1">
            <Link href={`/users/${ticket.user_id}`}>
              <span className="text-primary hover:underline cursor-pointer">
                {ticket.email}
              </span>
            </Link>
          </div>
          {ticket.first_name && (
            <div className="text-xs text-muted-foreground mt-0.5">
              {[ticket.first_name, ticket.last_name].filter(Boolean).join(" ")}
            </div>
          )}
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-sm text-muted-foreground">Категория</div>
          <div className="text-sm font-medium mt-1">{getCategoryLabel(ticket.category)}</div>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-sm text-muted-foreground">Источник</div>
          <div className="text-sm font-medium mt-1">{getSourceLabel(ticket.source)}</div>
          {ticket.client_name ? (
            <div className="text-xs text-muted-foreground mt-0.5">{ticket.client_name}</div>
          ) : null}
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-sm text-muted-foreground">Создан</div>
          <div className="text-sm font-medium mt-1">{formatDateTime(ticket.created_at)}</div>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-sm text-muted-foreground">Назначен на</div>
          <div className="text-sm font-medium mt-1">
            {ticket.assigned_to
              ? adminsList?.find((a) => a.id === ticket.assigned_to)?.email || ticket.assigned_to
              : "—"}
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2 rounded-md border border-input bg-background text-sm"
          >
            <option value="open">Открыт</option>
            <option value="in_progress">В работе</option>
            <option value="waiting_user">Ожидает ответа</option>
            <option value="resolved">Решён</option>
            <option value="closed">Закрыт</option>
          </select>
          <button
            onClick={() => statusMutation.mutate()}
            disabled={statusMutation.isPending || selectedStatus === ticket.status}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {statusMutation.isPending ? "..." : "Изменить статус"}
          </button>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={selectedAdmin}
            onChange={(e) => setSelectedAdmin(e.target.value)}
            className="px-3 py-2 rounded-md border border-input bg-background text-sm"
          >
            <option value="">Не назначен</option>
            {adminsList?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.first_name || a.email} ({a.role})
              </option>
            ))}
          </select>
          <button
            onClick={() => assignMutation.mutate()}
            disabled={assignMutation.isPending || selectedAdmin === (ticket.assigned_to || "")}
            className="px-4 py-2 border border-input rounded-md text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50"
          >
            {assignMutation.isPending ? "..." : "Назначить"}
          </button>
        </div>
      </div>

      {/* Messages Thread */}
      <div className="rounded-lg border border-border bg-card">
        <div className="p-4 border-b border-border">
          <h2 className="font-semibold">Переписка ({messages.length})</h2>
        </div>
        <div className="p-4 space-y-3 max-h-[500px] overflow-y-auto">
          {messages.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              Нет сообщений
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`p-3 rounded-lg ${
                  msg.is_support
                    ? "bg-primary/10 mr-8"
                    : "bg-muted ml-8"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium">
                    {msg.is_support ? "Поддержка" : (msg.user_email || "Пользователь")}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(msg.created_at)}
                  </span>
                </div>
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                {msg.attachments?.telegram?.chatId && msg.attachments.telegram.messageId ? (
                  <MediaAttachment
                    chatId={msg.attachments.telegram.chatId}
                    messageId={msg.attachments.telegram.messageId}
                  />
                ) : null}
              </div>
            ))
          )}
        </div>

        {/* Reply Form */}
        {!isClosed && (
          <div className="p-4 border-t border-border">
            <div className="flex gap-2">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Введите ответ..."
                className="flex-1 px-3 py-2 rounded-md border border-input bg-background text-sm min-h-[80px] resize-y focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <button
                onClick={() => replyMutation.mutate()}
                disabled={!replyText.trim() || replyMutation.isPending}
                className="self-end px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                {replyMutation.isPending ? "..." : "Отправить"}
              </button>
            </div>
            {replyMutation.error && (
              <div className="text-sm text-destructive mt-2">
                {(replyMutation.error as Error).message}
              </div>
            )}
          </div>
        )}

        {isClosed && (
          <div className="p-4 border-t border-border text-center text-sm text-muted-foreground">
            Тикет закрыт. Для ответа измените статус.
          </div>
        )}
      </div>
    </div>
  );
}
